import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import config from '@plone/volto/registry';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Editor, Node, Range, Transforms, createEditor } from 'slate';
import { Editable, ReactEditor, Slate, withReact } from 'slate-react';
import SubjectSidebar from './Sidebar';

const messages = defineMessages({
  typeSubject: {
    id: 'Type the subject…',
    defaultMessage: 'Type the subject…',
  },
});

function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export const Edit = (props) => {
  const {
    block,
    blockNode,
    data,
    detached,
    editable,
    index,
    onAddBlock,
    onChangeBlock,
    onFocusNextBlock,
    onFocusPreviousBlock,
    onSelectBlock,
    selected,
  } = props;

  const [editor] = useState(withReact(createEditor()));
  const [initialValue] = useState([
    {
      type: 'span',
      children: [{ text: data?.['subjectContent'] || '' }],
    },
  ]);

  const intl = useIntl();

  const prevSelected = usePrevious(selected);

  const subject = useMemo(() => data?.['subjectContent'] || '', [data]);

  const placeholder = useMemo(
    () => data.placeholder || intl.formatMessage(messages.typeSubject),
    [data.placeholder, intl],
  );
  const disableNewBlocks = useMemo(() => detached, [detached]);

  useEffect(() => {
    if (!prevSelected && selected) {
      if (editor.selection && Range.isCollapsed(editor.selection)) {
        // keep selection
        ReactEditor.focus(editor);
      } else {
        // nothing is selected, move focus to end
        ReactEditor.focus(editor);
        Transforms.select(editor, Editor.end(editor, []));
      }
    }
  }, [prevSelected, selected, editor]);

  useEffect(() => {
    // undo/redo handler
    const oldText = Node.string(editor);
    if (oldText !== subject) {
      Transforms.insertText(editor, subject, {
        at: [0, 0],
      });
    }
  }, [editor, subject]);

  const handleSubjectChange = useCallback(() => {
    const newText = Node.string(editor);
    if (newText !== subject) {
      onChangeBlock(block, { ...data, subjectContent: newText });
    }
  }, [editor, subject, onChangeBlock, block, data]);

  const handleKeyDown = useCallback(
    (ev) => {
      if (ev.key === 'Return' || ev.key === 'Enter') {
        ev.preventDefault();
        if (!disableNewBlocks) {
          onSelectBlock(
            onAddBlock(config.settings.defaultBlockType, index + 1),
          );
        }
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        onFocusPreviousBlock(block, blockNode.current);
      } else if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        onFocusNextBlock(block, blockNode.current);
      }
    },
    [
      index,
      blockNode,
      disableNewBlocks,
      onSelectBlock,
      onAddBlock,
      onFocusPreviousBlock,
      onFocusNextBlock,
      block,
    ],
  );

  const handleFocus = useCallback(() => {
    onSelectBlock(block);
  }, [block, onSelectBlock]);

  const renderSubject = useCallback(
    ({ attributes, children }) => {
      const show_alignment =
        !config.blocks?.blocksConfig?.subject?.hide_alignment;

      return (
        <div
          {...attributes}
          style={show_alignment ? { textAlign: data.alignment } : {}}
          className="head-title edit-mode"
        >
          {children}
        </div>
      );
    },
    [data.alignment],
  );

  if (typeof window.__SERVER__ !== 'undefined') {
    return <div />;
  }
  return (
    <>
      <Slate
        editor={editor}
        onChange={handleSubjectChange}
        initialValue={initialValue}
      >
        <Editable
          readOnly={!editable}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          renderElement={renderSubject}
          onFocus={handleFocus}
          aria-multiline="false"
        ></Editable>
      </Slate>
      <SidebarPortal selected={selected}>
        <SubjectSidebar
          {...props}
          data={data}
          block={block}
          onChangeBlock={props.onChangeBlock}
        />
      </SidebarPortal>
    </>
  );
};

Edit.propTypes = {
  properties: PropTypes.objectOf(PropTypes.any).isRequired,
  selected: PropTypes.bool.isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onChangeField: PropTypes.func.isRequired,
  onSelectBlock: PropTypes.func.isRequired,
  onDeleteBlock: PropTypes.func.isRequired,
  onAddBlock: PropTypes.func.isRequired,
  onFocusPreviousBlock: PropTypes.func.isRequired,
  onFocusNextBlock: PropTypes.func.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  editable: PropTypes.bool,
  detached: PropTypes.bool,
  blockNode: PropTypes.any,
};

Edit.defaultProps = {
  detached: false,
  editable: true,
};

export default Edit;
